import * as React from 'react';
import { type PropsWithChildren, createContext, useContext, useMemo, useState } from 'react';

import type { ValidateBusinessEmailAction } from 'bundles/teams-v2/constants/businessEmail';
import type { ValueOf } from 'bundles/teams-v2/types/sharedTypes';

type BusinessEmailValidationState = {
  email: string | null;
  isLoading: boolean;
  status: ValueOf<typeof ValidateBusinessEmailAction> | null;
};

type BusinessEmailValidationActions = {
  startValidation: (email: string) => void;
  updateValidationStatus: (status: ValueOf<typeof ValidateBusinessEmailAction>) => void;
};

const BusinessEmailValidationStateContext = createContext<BusinessEmailValidationState | undefined>(undefined);
const BusinessEmailValidationActionsContext = createContext<BusinessEmailValidationActions | undefined>(undefined);

/**
 * Provider component for business email validation contexts
 * Uses separate contexts for state and actions to prevent unnecessary re-renders
 * - Components that only need state won't re-render when actions are used
 * - Components that only need actions won't re-render when state changes
 */
export const BusinessEmailValidationProvider = ({ children }: PropsWithChildren) => {
  const [state, setState] = useState<BusinessEmailValidationState>({
    email: null,
    isLoading: false,
    status: null,
  });

  // Memoize actions to prevent unnecessary re-renders
  const actions = useMemo<BusinessEmailValidationActions>(
    () => ({
      startValidation: (email: string) => {
        setState((prevState) => ({
          ...prevState,
          email,
          isLoading: true,
          status: null,
        }));
      },

      updateValidationStatus: (status: ValueOf<typeof ValidateBusinessEmailAction>) => {
        setState((prevState) => ({
          ...prevState,
          status,
          isLoading: false,
        }));
      },
    }),
    []
  );

  return (
    <BusinessEmailValidationStateContext.Provider value={state}>
      <BusinessEmailValidationActionsContext.Provider value={actions}>
        {children}
      </BusinessEmailValidationActionsContext.Provider>
    </BusinessEmailValidationStateContext.Provider>
  );
};

/**
 * Hook to access business email validation state
 * Components using this will only re-render when state changes
 */
export const useBusinessEmailValidationState = (): BusinessEmailValidationState => {
  const context = useContext(BusinessEmailValidationStateContext);
  if (context === undefined) {
    throw new Error('useBusinessEmailValidationState must be used within a BusinessEmailValidationProvider');
  }
  return context;
};

/**
 * Hook to access business email validation actions
 * Components using this will never re-render (actions are memoized)
 */
export const useBusinessEmailValidationActions = (): BusinessEmailValidationActions => {
  const context = useContext(BusinessEmailValidationActionsContext);
  if (context === undefined) {
    throw new Error('useBusinessEmailValidationActions must be used within a BusinessEmailValidationProvider');
  }
  return context;
};

/**
 * Convenience hook that provides both state and actions
 * Use only when component needs both - otherwise use specific hooks above for better performance
 */
export const useBusinessEmailValidation = () => {
  const state = useBusinessEmailValidationState();
  const actions = useBusinessEmailValidationActions();

  return {
    state,
    ...actions,
  };
};

export default BusinessEmailValidationStateContext;
